import React, { useEffect, useRef } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { Auth } from "aws-amplify";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { I18n } from "aws-amplify";
import { authApi } from "../../apis";
import { REGEX, SCREEN_PATHS, STORAGE } from "../../constants";
import { getQueryStringParams } from "@theatticusapp/atticus-shared-components";
import { LocaleInputs } from "./LocaleInputs";
import { AuthHeader } from "./AuthHeader";
import { AuthFooter } from "./AuthFooter";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  CONSTANTS as SHARED_CONSTANTS,
  GoogleAnalytics,
} from "@theatticusapp/atticus-shared-components";
import * as flows from "../flows";
import { EnvironmentInputs } from "./EnvironmentInputs";
import { PreProdOnlyWrapper } from "../shared/PreProdOnlyWrapper";

// https://ui.docs.amplify.aws/components/authenticator#internationalization-i18n
I18n.putVocabulariesForLanguage("en", {
  "Create Account": "Sign Up", // Tab header
  "Given Name": "First Name",
  "Family Name": "Last Name",
  "Sign Up": "Create your free account",
  "We Emailed You": "Check your inbox!",
  "Your code is on the way. To log in, enter the code we emailed to":
    "We just sent a short code to",
  "It may take a minute to arrive.":
    "Just grab that and type it into the box below to log in. It may take a minute to arrive.",
  "User does not exist.":
    "Oops! We can’t find an Atticus account with that email address.",
  "Network error":
    "Oops! It looks like your internet connection isn’t working. Let’s try again when it’s back online.",
  "Invalid verification code provided, please try again.":
    "Oops! That code doesn't look right. Please try again!",
  Confirm: "Confirm your email address",
  "Resend Code": "Resend code",
});

const AuthenticatorWrapper = styled("div")(({ theme }) => ({
  // https://ui.docs.amplify.aws/components/authenticator#styling
  "[data-amplify-container]": {
    width: "100%",
  },
  ".amplify-button[type=submit]": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "24px !important", // * hack required because aws is adding an inline style
    textTransform: "none",
    minWidth: 180,
    boxShadow: "none",
    fontSize: 16,
    fontWeight: "bold !important", // *
    padding: "10px 16px",
    margin: "10px 0",
  },
  ".amplify-button[type=submit] + .amplify-button[type=button]": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    borderRadius: "24px !important", // * hack required because aws is adding an inline style
    borderColor: theme.palette.primary.main,
    borderWidth: "1px",
    textTransform: "none",
    minWidth: 180,
    boxShadow: "none",
    fontSize: 16,
    fontWeight: "bold !important", // *
    padding: "10px 16px",
    margin: "10px 0",
  },
  ".federated-sign-in-button": {
    borderRadius: 20,
    padding: "6px 16px",
  },
  "[data-amplify-router]": {
    boxShadow: "none",
  },
  ".amplify-tabs": {
    borderWidth: 0,
    // backgroundColor: "#F2F2FF",
    position: "relative",
    top: -18,
  },
  ".amplify-tabs-item": {
    color: theme.palette.grey[500],
    borderRadius: 28,
    border: "none",
  },
  "div.amplify-tabs-item:nth-of-type(1)": {
    borderRadius: "28px 28px 28px 0",
  },
  "div.amplify-tabs-item:nth-of-type(2)": {
    borderRadius: "28px 28px 0 28px",
  },
  ".amplify-tabs-item[data-state=inactive]": {
    backgroundColor: theme.palette.grey[200],
  },
  ".amplify-tabs-item[data-state=inactive]:hover": {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
  },
  ".amplify-tabs-item[data-state=active]": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
  },
  ".amplify-textfield": {
    marginTop: "8px",
    marginBottom: "8px",
  },
  ".amplify-button[data-variation=link]": {
    color: theme.palette.primary.main,
  },
  ".amplify-button[data-variation=link]:hover": {
    backgroundColor: theme.palette.background.default,
    textDecoration: "underline",
  },
  "h3.amplify-heading": {
    fontSize: "var(--amplify-components-heading-4-font-size)",
    fontWeight: "bold",
  },
  // hide the sms account verification
  "[data-amplify-authenticator-verifyuser] .amplify-radiogroup label:nth-of-type(2)":
    {
      display: "none",
    },
}));

interface LocationState {
  path: string;
  search: string;
  hash: string;
}

export const HostedAuth = (): JSX.Element => {
  const navigate = useNavigate();
  const locationState = useLocation().state as LocationState;

  const params = getQueryStringParams();
  // if user came from trying to view a form in the forms app we
  // let them get one free first form via ?firstFormId=X
  const firstFreeFormId: string | undefined = params?.firstFormId;
  const initAsSignUp = params?.signup === "true";
  const origin = params?.origin || undefined;
  const initEmail = params?.email;
  const includeState = params?.incState === "true";

  useEffect(() => {
    if (initAsSignUp && initEmail) {
      prepopulateSignup();
    }
  }, []);

  const generatePassword = (triesLeft = 3): string => {
    const password = new Array(12)
      .fill(1)
      .map(() => String.fromCharCode(Math.random() * 86 + 40))
      .join("");
    // Since the passwords is created from random string of chars it's possible it won't meet the requirements.
    //  Confirm the password generated fits the required criteria, otherwise create another.
    if (REGEX.password.test(password)) {
      return password;
    } else {
      // didn't pass, try again
      return triesLeft > 0 ? generatePassword(triesLeft - 1) : "";
    }
  };

  const prepopulateSignup = (triesLeft = 3) => {
    if (triesLeft > 0) {
      // slight delay to allow Authenticator widget to load
      setTimeout(() => {
        // find and inject the values into the Authenticator form inputs
        const emailInput = document.getElementsByName("email");
        const passwordInput = document.getElementsByName("password");
        const confirmPasswordInput =
          document.getElementsByName("confirm_password");
        if (emailInput?.length) {
          (emailInput[0] as HTMLInputElement).value = initEmail;
          const generatedPassword = generatePassword();
          if (passwordInput?.length) {
            (passwordInput[0] as HTMLInputElement).value = generatedPassword;
          }
          if (confirmPasswordInput?.length) {
            (confirmPasswordInput[0] as HTMLInputElement).value =
              generatedPassword;
          }
        } else {
          prepopulateSignup(triesLeft - 1);
        }
      }, 200);
    }
  };

  const preSelectVerify = (triesLeft = 3) => {
    if (triesLeft > 0) {
      // slight delay to allow Authenticator widget to load
      setTimeout(() => {
        // find and select the verification radio in Authenticator
        const verifyOptions = document.getElementsByName("unverifiedAttr");
        if (verifyOptions?.length) {
          (verifyOptions[0] as HTMLInputElement).checked = true;
        } else {
          preSelectVerify(triesLeft - 1);
        }
      }, 200);
    }
  };

  // const [stateIsSignUp, setStateIsSignUp] = useState(initAsSignUp);

  // if they selected a first free form save it so it can be added to the estate after onboarding is done
  if (firstFreeFormId) {
    window.sessionStorage.setItem(STORAGE.firstFreeFormId, firstFreeFormId);
  }
  // const email = params?.email || undefined; // when Authenticator allows pre-filling fields
  let inviteId = window.sessionStorage.getItem(STORAGE.invite);
  const partnerInviteId = Cookies.get(SHARED_CONSTANTS.COOKIES.inviteId);
  if (!inviteId && partnerInviteId) {
    // inviteId from sessionStorage takes precedent, apply partnerInviteId if present and no inviteId
    inviteId = partnerInviteId;
  }
  // The following will be used to access the country and language values in LocaleInputs
  const countryInput = useRef(null);
  const stateInput = useRef(null);
  const languageInput = useRef(null);

  console.log("inviteId:", inviteId);
  // https://ui.docs.amplify.aws/components/authenticator
  const authHandlers = {
    // NOTE: this is used, but transparent to static code checking
    async handleSignIn(formData: any): Promise<any> {
      GoogleAnalytics.pushEvent({ title: "auth sign in attempt" });
      const { username, password } = formData;
      try {
        const cognitoUser = await Auth.signIn({
          username,
          password,
        });
        await authApi.getAndSetAuthToken();
        GoogleAnalytics.pushEvent({ title: "sign in success" });
        return cognitoUser;
      } catch (error) {
        console.log(error);
        // migration code removed march 2023 - customers have had more than 1 year to migrate
        throw error; // authenticator will handle the error
      }
    },

    async handleSignUp(formData: any) {
      GoogleAnalytics.pushEvent({ title: "auth sign up attempt" });
      const { username, password, attributes } = formData;
      // validate phone
      const phoneRegex = new RegExp(REGEX.phone);
      if (!phoneRegex.test(attributes["phone_number"])) {
        return Promise.reject(new Error("Please check the Phone Number."));
      }
      // fix some short-comings of the Authenticator phone field
      attributes["phone_number"] = attributes["phone_number"]
        .replace("undefined", "+1")
        .replace("+11", "+1");
      
      // It's unclear if we ever needed invite id here.
      // If you get an invite id stored via an advisor signup link,
      // then this might mess up your ability to create a new account.
      // On the other hand, we do have code somewhere that somehow invites
      // users to our platform on behalf of an advisor, so it's possible
      // we may want to re-enable this code.
      // if (inviteId) {
      //   attributes["custom:invite_id"] = inviteId;
      // }
      
      if (origin) {
        attributes["custom:origin"] = origin;
      }
      // NOTE: the following is sort of ugly, but I don't see an alternative
      if (countryInput.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const countryId = countryInput.current.children.country.value;
        attributes["custom:country_id"] = countryId.toString();
      }
      if (stateInput.current) {
        // stateInput will be present when param incState=true - advisor signing up customer
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const stateId = stateInput.current.children.state.value;
        if (stateId === "0") {
          return Promise.reject(
            new Error("Please choose a State/Province/Territory")
          );
        }
        attributes["custom:state_id"] = stateId.toString();
      }
      if (languageInput.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const languageId = languageInput.current.children.language.value;
        attributes["custom:language_id"] = languageId.toString();
      }
      const signUpResponse = await Auth.signUp({
        username,
        password,
        attributes,
      });
      GoogleAnalytics.pushEvent({ title: "auth sign up success" });
      return signUpResponse;
    },

    async handleConfirmSignIn({
      user,
      code,
    }: {
      user: any;
      code: string;
    }): Promise<any> {
      GoogleAnalytics.pushEvent({ title: "auth confirm sign in attempt" });
      const confirmSignInResponse = await Auth.confirmSignIn(user, code);
      GoogleAnalytics.pushEvent({ title: "auth confirm sign in success" });
      return confirmSignInResponse;
    },

    async handleConfirmSignUp({
      username,
      code,
    }: {
      username: string;
      code: string;
    }): Promise<any> {
      GoogleAnalytics.pushEvent({ title: "auth confirm sign up attempt" });
      const confirmSignUpResponse = await Auth.confirmSignUp(username, code);
      GoogleAnalytics.pushEvent({ title: "auth confirm sign up success" });
      return confirmSignUpResponse;
    },
  };

  const authComponents = {
    Footer: () => <AuthFooter />,
    Header: () => <AuthHeader />,
    SignIn: {
      Header() {
        // setStateIsSignUp(false);
        return <></>;
      },
    },
    SignUp: {
      // NOTE: this is used, but transparent to static code checking
      FormFields() {
        return (
          <>
            {/* input for fields sent into Authenticator signUpAttributes attr below */}
            <Authenticator.SignUp.FormFields />
            <LocaleInputs
              countryRef={countryInput}
              stateRef={includeState ? stateInput : undefined}
              languageRef={languageInput}
            />
          </>
        );
      },
      Header() {
        // setStateIsSignUp(true);
        return (
          <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
            Try Atticus for free. Cancel anytime.
          </Typography>
        );
      },
    },
    ResetPassword: {
      Header() {
        return (
          <>
            <Typography variant="h3">Reset your password</Typography>
            <Typography variant="body1">
              Please enter the email address associated with your Atticus
              account.
            </Typography>
          </>
        );
      },
    },
    VerifyUser: {
      Header() {
        preSelectVerify();
        return <></>;
      },
    },
  };

  const formFields = {
    signUp: {
      confirm_password: {
        labelHidden: false,
        label:
          "Password must include a number, an upper case, a lower case and a special character. Example: P4ssw*rd",
      },
      given_name: {
        labelHidden: true,
        isRequired: false,
      },
      // middle_name: {
      //   labelHidden: true,
      // },
      family_name: {
        labelHidden: true,
      },
      phone_number: {
        labelHidden: true,
      },
    },
  };

  return (
    <>
      <Card
        sx={{
          border: "none",
        }}
      >
        <CardContent
          sx={{
            p: 0,
          }}
        >
          <AuthenticatorWrapper>
            {/* https://ui.docs.amplify.aws/components/authenticator */}
            <Authenticator
              services={authHandlers}
              initialState={initAsSignUp ? "signUp" : "signIn"}
              signUpAttributes={[
                "given_name",
                // "middle_name",
                "family_name",
                "phone_number",
              ]}
              formFields={formFields}
              // TODO: configure google auth: https://docs.aws.amazon.com/cognito/latest/developerguide/google.html
              // socialProviders={["google"]}
              components={authComponents}
            >
              {() => {
                (async () => {
                  // set the auth token for the laravel call
                  await authApi.getAndSetAuthToken();
                  if (locationState?.path) {
                    const redirectBack = `${locationState?.path}${locationState?.hash}${locationState?.search}`;
                    const isInFlow = locationState?.path.includes(
                      SCREEN_PATHS.flow,
                      0
                    );
                    if (isInFlow) {
                      const allFlows = Object.values(flows);
                      const currentFlow = allFlows.find((flow) => {
                        return location.pathname.split("/")[2] == flow.flowPath;
                      });
                      navigate(
                        currentFlow?.getFlowFirstScreen() || redirectBack
                      );
                    } else {
                      navigate(redirectBack);
                    }
                  } else {
                    navigate(SCREEN_PATHS.home);
                  }
                })();
                return <></>;
              }}
            </Authenticator>
          </AuthenticatorWrapper>
        </CardContent>
      </Card>
      <PreProdOnlyWrapper>
        <EnvironmentInputs />
      </PreProdOnlyWrapper>
    </>
  );
};
